/**
 * Datas do dashboard, sempre no fuso da operação.
 *
 * Dois problemas que este módulo resolve:
 *
 * 1. **O que é enviado para a API.** O backend recorta o período por dia do
 *    calendário em São Paulo. Mandar `toISOString()` transformava a escolha do
 *    usuário num instante UTC, que o servidor tinha de adivinhar de volta — e
 *    adivinhava no fuso do processo dele. Agora vai o dia literal
 *    ('2026-09-17'), sem ambiguidade.
 * 2. **O que é exibido.** As datas vindas da API são instantes; formatá-las sem
 *    declarar o fuso mostra a hora da máquina de quem abriu o dashboard.
 */

export const APP_TIMEZONE = 'America/Sao_Paulo';

/** Dia de hoje no fuso de operação, independente do fuso do navegador. */
const todayParts = () => {
  const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(new Date())
    .split('-')
    .map(Number);

  return { year, month, day };
};

/**
 * Hoje, como Date de meia-noite local.
 *
 * O calendário (react-datepicker) trabalha com Date no fuso do navegador, então
 * o dia de São Paulo é carregado como meia-noite local: o componente mostra a
 * data certa e `toApiDay` devolve exatamente esse dia.
 */
export const today = () => {
  const { year, month, day } = todayParts();
  return new Date(year, month - 1, day);
};

/** N dias antes de hoje (no fuso de operação). */
export const daysAgo = (days) => {
  const { year, month, day } = todayParts();
  return new Date(year, month - 1, day - days);
};

/** Primeiro dia do mês corrente (no fuso de operação). */
export const startOfMonth = () => {
  const { year, month } = todayParts();
  return new Date(year, month - 1, 1);
};

export const isSameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();

/**
 * Formato aceito pela API: o dia do calendário como o usuário o escolheu.
 * Usa os componentes locais de propósito — `toISOString()` converteria para UTC
 * e, à noite, pularia para o dia seguinte.
 */
export const toApiDay = (date) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/** Data e hora de exibição, fixadas no fuso de operação. */
export const formatDateTime = (value) => {
  if (!value) return '';

  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return '';

  return instant.toLocaleString('pt-BR', { timeZone: APP_TIMEZONE });
};

/** Só a data, no fuso de operação. */
export const formatDate = (value) => {
  if (!value) return '';

  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return '';

  return instant.toLocaleDateString('pt-BR', { timeZone: APP_TIMEZONE });
};
